import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import CustomTypography from '../../CustomComponents/CustomTypography';
import PageTitle from '../../../Utils/PageTitle';
import moment from 'moment';
import { formatDate } from '../../../Utils/dateFormat';
import AuditDetailPanel from './AuditDetailPanel';
import AuditList from './AuditList';
import AuditUserDetail from './AuditUserDetail';
import AuditFacilityDetail from './AuditFacilityDetail';
import {
  JOB_COLUMNS,
  USER_COLUMNS,
  FACILITY_COLUMNS
} from './auditListColumns';
import { createAuditColumns } from './auditTanstackColumnConfig';
import { SplitShell, Splitter } from './AuditDetailStyles';
import { TitleRow, ContainerRow, SearchInputStyle } from '../styledComponents';
import { useSelector } from 'react-redux';
import { RESULTS_PER_PAGE } from '../../../Constants/StringConstants';
import { useDebounce } from '../../../Utils/Debounce';
import { SettingOutlined } from '@ant-design/icons';
import { strikeDate } from '../../../Apis/Jobs';
import ConfirmationModal from '../../CustomComponents/ConfirmationModal';
import styled from 'styled-components';
import AuditColumnSettingsModal, {
  DEFAULT_JOB_VISIBLE_COLUMNS,
  DEFAULT_USER_VISIBLE_COLUMNS
} from './AuditColumnSettingsModal';
import {
  getColumnPreferences,
  saveColumnPreferences
} from '../../../Apis/Clinic';
import {
  StyledRangePicker,
  StyledSelect,
  StyledDivOptions
} from './styledComponents';
import {
  StyledJobListTab,
  StyledJobListTabPaneContent,
  StyledJobListTabPaneContentText,
  StyledTableControlContainer
} from '../JobList/styledComponents';
import { notification, Select, Button, Tabs } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
  GetSessionDetails,
  fetchJobsForAudit,
  fetchFacilityAudits
} from '../../../Apis/Facility';
import { useReactToPrint } from 'react-to-print';
import FacilityFilter from '../../CustomComponents/FacilityFilter';
import { useHistory } from 'react-router-dom';

const { TabPane } = Tabs;
const { Option } = StyledSelect;
const pageSize = RESULTS_PER_PAGE;

// Audit tabs are selection-only (empty TabPanes); drop the default tab-nav
// bottom margin and the empty content holder to remove the gap before the
// table controls.
const AuditTabs = styled(StyledJobListTab)`
  & .ant-tabs-nav {
    margin-bottom: 0;
  }
  & .ant-tabs-content-holder {
    display: none;
  }
`;

const Audit = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [searchKey, setSearchKey] = useState('name');
  const [dateRange, setDateRange] = useState({
    startTimeFilter: moment().subtract(2, 'months').startOf('day').valueOf(),
    endTimeFilter: moment().endOf('day').valueOf()
  });
  const [sortOrder, setSortOrder] = useState('DESCEND');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [sortKey, setSortKey] = useState('loginTime');
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  // Resizable width (px) of the compact job-list column in the audit split view.
  const AUDIT_LIST_MIN_WIDTH = 280;
  const AUDIT_LIST_MAX_WIDTH = 1200;
  const [auditListWidth, setAuditListWidth] = useState(1000);
  const splitShellRef = useRef(null);
  const startAuditListResize = useCallback(e => {
    e.preventDefault();
    const onMove = ev => {
      const rect = splitShellRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next = Math.min(
        AUDIT_LIST_MAX_WIDTH,
        Math.max(AUDIT_LIST_MIN_WIDTH, ev.clientX - rect.left)
      );
      setAuditListWidth(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (splitShellRef.current) {
        splitShellRef.current.classList.remove('resizing');
      }
    };
    if (splitShellRef.current) {
      splitShellRef.current.classList.add('resizing');
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);
  const [sessionData, setSessionData] = useState([]);
  const [facilityAuditData, setFacilityAuditData] = useState([]);
  const [isModalVisible, setModalVisible] = useState(false);
  const firstRender = useRef(true);
  const selectedTabRef = useRef(selectedTab);
  const [datePickerValue, setDatePickerValue] = useState([
    moment().subtract(2, 'months').startOf('day'),
    moment().endOf('day')
  ]);
  const [facilities, setFacilities] = useState();
  const dateSelectionCompleted = useRef(false);
  const componentRef = useRef();
  const history = useHistory();
  const user = useSelector(state => state.userReducer);
  const [printOption, setPrintOption] = useState('audit');
  const userString = localStorage.getItem('user');

  // Parse the user data from JSON string to object
  const userType = userString ? JSON.parse(userString) : null;
  const sessionId = userType ? userType.sessionId : null;

  const [selectedTab, setSelectedTab] = useState(1);
  const [releasetModalVisible, setReleaseModalVisible] = useState(false);
  const [rowToBeStriked, setRowToBeStriked] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  // const [currentSessionId, setCurrentSessionId] = useState(null);

  const [releaseModalLoading, setReleaseModalLoading] = useState(false);
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const [exportFunction, setExportFunction] = useState(null);
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    DEFAULT_JOB_VISIBLE_COLUMNS
  );
  const openModal = () => {
    if (currentJobId && sessionId) {
      strikeDate(currentJobId, sessionId)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Successful',
              description: 'Job has been stricken out successfully.'
            });
            fetchAll(currentPage);
          }
        })

        .catch(error => {
          // Handle error if needed
          console.error('API call error', error);

          // Show error notification
          notification['error']({
            message: 'Strike Failed',
            description:
              'Failed to perform the strike operation. Please try again.'
          });
        });
    }
    setReleaseModalVisible(false);
  };
  const handleStartButtonClick = useCallback(jobId => {
    setCurrentJobId(jobId);
    // setCurrentSessionId(sessionId);
    setReleaseModalVisible(true);
  }, []);

  const getPageName = tab =>
    tab === 1 ? 'AUDIT_JOB_LIST' : 'AUDIT_LOGIN_LIST';

  const fetchColumnPreferences = useCallback(async tab => {
    const defaultColumns =
      tab === 1 ? DEFAULT_JOB_VISIBLE_COLUMNS : DEFAULT_USER_VISIBLE_COLUMNS;
    try {
      const response = await getColumnPreferences(getPageName(tab));
      if (
        response.data?.result &&
        Array.isArray(response.data.result) &&
        response.data.result.length > 0
      ) {
        const prefs = response.data.result.reduce((acc, item) => {
          if (item.columnName && item.columnName in defaultColumns) {
            acc[item.columnName] = item.selected;
          }
          return acc;
        }, {});
        setVisibleColumns({
          ...defaultColumns,
          ...prefs,
          ...(tab === 1
            ? { jobNumber: true }
            : { fullName: true, profilePic: true })
        });
      } else {
        setVisibleColumns(defaultColumns);
      }
    } catch {
      setVisibleColumns(defaultColumns);
    }
  }, []);

  const handleSaveColumnSettings = newVisibleColumns => {
    setVisibleColumns(newVisibleColumns);
    const columnPreferencesArray = Object.keys(newVisibleColumns).map(
      columnName => ({ columnName, selected: newVisibleColumns[columnName] })
    );
    saveColumnPreferences(getPageName(selectedTab), columnPreferencesArray)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Column preferences saved successfully'
        });
      })
      .catch(() => {
        notification.error({
          message: 'Error',
          description: 'Failed to save column preferences. Please try again.'
        });
      });
  };
  const fetchAll = pageNo => {
    setLoading(true);
    const tabAtCallTime = selectedTab;
    selectedTabRef.current = selectedTab;
    let payload = {
      page: pageNo,
      searchKey: searchKey,
      searchValue: searchValue,
      size: pageSize,
      sortKey: [sortKey],
      sortOrder: [sortOrder],
      userTypeFilter: userTypeFilter
    };
    if (facilities && facilities.length > 0) {
      payload['facilityIds'] = facilities;
    }
    if (selectedTab === 1) {
      const jobsPayload = {
        submitTimeFrom: dateRange ? dateRange.startTimeFilter : null,
        submitTimeTo: dateRange ? dateRange.endTimeFilter : null
      };
      fetchJobsForAudit(jobsPayload)
        .then(response => {
          if (selectedTabRef.current !== tabAtCallTime) return;
          if (response.data.success) {
            setSessionData(response.data.result);
            setTotal(response.data.size);
            setLoading(false);
          } else {
            notification['error']({
              message: 'Session Fetch Failed',
              description: response.data.message
            });
            setLoading(false);
          }
        })

        .catch(err => {
          if (selectedTabRef.current !== tabAtCallTime) return;
          setLoading(false);
          console.log(err);
        });
    } else if (selectedTab === 2) {
      const usersPayload = {
        loginTimeFrom: dateRange ? dateRange.startTimeFilter : null,
        loginTimeTo: dateRange ? dateRange.endTimeFilter : null
      };
      GetSessionDetails(usersPayload)
        .then(response => {
          if (selectedTabRef.current !== tabAtCallTime) return;
          if (response.data.success) {
            setSessionData(response.data.result);
            setTotal(response.data.size);
            setLoading(false);
          } else {
            notification['error']({
              message: 'Session Fetch Failed',
              description: response.data.message
            });
            setLoading(false);
          }
        })
        .catch(err => {
          if (selectedTabRef.current !== tabAtCallTime) return;
          setLoading(false);
          console.log(err);
        });
    } else {
      fetchFacilityAudits(pageNo, pageSize)
        .then(response => {
          if (selectedTabRef.current !== tabAtCallTime) return;
          if (response.data.success) {
            setFacilityAuditData(response.data.result);
            setTotal(response.data.size);
            setLoading(false);
          } else {
            notification['error']({
              message: 'Session Fetch Failed',
              description: response.data.message
            });
            setLoading(false);
          }
        })
        .catch(err => {
          if (selectedTabRef.current !== tabAtCallTime) return;
          setLoading(false);
          console.log(err);
        });
    }
  };

  const pageNoChangeHandler = pageNo => {
    fetchAll(pageNo);
  };
  // const printOptions = [
  //   { label: 'Print Note', value: 'note' },
  //   { label: 'Print Audit', value: 'audit' },
  //   { label: 'Print Note with Audit', value: 'noteWithAudit' }
  // ];
  // const handlePrintChange = value => {
  //   switch (value) {
  //     case 'note':
  //       // Implement logic to print the note
  //       break;
  //     case 'audit':
  //       // Implement logic to print the audit
  //       break;
  //     case 'noteWithAudit':
  //       // Implement logic to print both note and audit
  //       break;
  //     default:
  //       break;
  //   }
  // };

  const onSearchChangeHandler = value => {
    setCurrentPage(0);
    setSearchValue(value);
  };

  const debounceOnChange = useCallback(
    useDebounce(onSearchChangeHandler, 1000),
    []
  );

  const disabledDate = current => {
    return current && current > moment().endOf('day');
  };

  const dateRangeChangeHandler = date => {
    if (date) {
      dateSelectionCompleted.current = true;
      setDatePickerValue(date);
      let range = {
        startTimeFilter: parseFloat(date[0].startOf('day').format('x')),
        endTimeFilter: parseFloat(date[1].endOf('day').format('x'))
      };
      setDateRange(range);
    } else {
      dateSelectionCompleted.current = false;
      setDatePickerValue(null);
      setDateRange(null);
    }
  };

  const handleDatePickerOpenChange = open => {
    if (open) {
      dateSelectionCompleted.current = false;
      if (datePickerValue) {
        setDatePickerValue(null);
      }
    } else {
      if (!dateSelectionCompleted.current && dateRange) {
        setDatePickerValue([
          moment(dateRange.startTimeFilter),
          moment(dateRange.endTimeFilter)
        ]);
      }
    }
  };

  const handleUserFilterChange = value => {
    setUserTypeFilter(value ? value : '');
  };

  const tableChangeHandler = (pagination, filters, sorter) => {
    if (sorter.field !== undefined) setSortKey(sorter.field && sorter.field[0]);
    if (sorter.order !== undefined) setSortOrder(sorter.order);
  };

  const onRow = (record, index, isModalVisible) => {
    return {
      onClick: event => {
        const clickedElement = event.target;

        if (clickedElement.innerText != 'Strike Out') {
          setSelectedSession(record);
          setDrawerVisible(true);
        }
      }
    };
  };

  const handleSelectedFacility = (record, index) => {
    return {
      onClick: event => {
        setSelectedFacility(record);
        setDrawerVisible(true);
      }
    };
  };

  useEffect(() => {
    if (firstRender.current === false) {
      setSelectedJob(null);
      setSelectedSession(null);
      setSelectedFacility(null);
      fetchAll(0);
      setCurrentPage(0);
    }
    // eslint-disable-next-line
  }, [
    dateRange,
    searchValue,
    sortOrder,
    userTypeFilter,
    searchKey,
    sortKey,
    facilities
  ]);

  useEffect(() => {
    selectedTabRef.current = selectedTab;
    if (
      firstRender.current === false &&
      (userTypeFilter.length > 0 ||
        searchValue.length > 0 ||
        searchKey === 'jobName')
    ) {
      setUserTypeFilter('');
      setSearchValue('');
      setSearchKey('name');
    } else {
      fetchAll(0);
      setCurrentPage(0);
    }
    setSessionData([]);
    setSelectedJob(null);
    setSelectedSession(null);
    setSelectedFacility(null);
    firstRender.current = false;
    if (selectedTab === 1 || selectedTab === 2) {
      fetchColumnPreferences(selectedTab);
    }
    // eslint-disable-next-line
  }, [selectedTab]);

  // const handlePrint = useReactToPrint({
  //   content: () => componentRef.current
  // });
  const handlePrint = useReactToPrint({
    content: () => {
      return componentRef.current;
    }
  });

  const viewJob = jobId => {
    history.push(
      `/dashboard/new-editor/${jobId}?jobStage=completed&userType=${user?.userType}`
    );
  };

  const auditColumns = useMemo(
    () => createAuditColumns(selectedTab, handleStartButtonClick),
    [selectedTab, handleStartButtonClick]
  );

  const auditDefaultSorting = useMemo(() => {
    if (selectedTab === 1) return [{ id: 'submissionTime', desc: true }];
    if (selectedTab === 2) return [{ id: 'loginTime', desc: true }];
    return [];
  }, [selectedTab]);

  const handleExportReady = useCallback(fn => setExportFunction(() => fn), []);

  return (
    <>
      <PageTitle title='Audit' />
      <ContainerRow>
        <TitleRow
          justify='space-between'
          style={{ paddingLeft: 4, alignItems: 'center', flexWrap: 'wrap', gap: 8 }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <CustomTypography color={theme['@text-color']}>
              Audit
            </CustomTypography>
          </div>
          <StyledDivOptions>
            {(selectedTab === 1 || selectedTab === 2) && (
              <StyledRangePicker
                disabledDate={disabledDate}
                onChange={dateRangeChangeHandler}
                onOpenChange={handleDatePickerOpenChange}
                value={datePickerValue}
              />
            )}
            <Button onClick={() => setClearFiltersTrigger(t => t + 1)}>
              Clear Filters
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportFunction && exportFunction()}
            >
              Export to Excel
            </Button>
          </StyledDivOptions>
        </TitleRow>
        <AuditTabs
          activeKey={String(selectedTab)}
          onChange={key => setSelectedTab(Number(key))}
        >
          {userType?.userType !== 'doctor' && (
            <>
              <TabPane
                tab={
                  <StyledJobListTabPaneContent>
                    <StyledJobListTabPaneContentText>
                      Jobs
                    </StyledJobListTabPaneContentText>
                  </StyledJobListTabPaneContent>
                }
                key='1'
              />
              <TabPane
                tab={
                  <StyledJobListTabPaneContent>
                    <StyledJobListTabPaneContentText>
                      Users
                    </StyledJobListTabPaneContentText>
                  </StyledJobListTabPaneContent>
                }
                key='2'
              />
              <TabPane
                tab={
                  <StyledJobListTabPaneContent>
                    <StyledJobListTabPaneContentText>
                      Facilities
                    </StyledJobListTabPaneContentText>
                  </StyledJobListTabPaneContent>
                }
                key='3'
              />
            </>
          )}
          {userType?.userType === 'doctor' && (
            <TabPane
              tab={
                <StyledJobListTabPaneContent>
                  <StyledJobListTabPaneContentText>
                    Jobs
                  </StyledJobListTabPaneContentText>
                </StyledJobListTabPaneContent>
              }
              key='1'
            />
          )}
        </AuditTabs>

        <SplitShell
          ref={splitShellRef}
          style={{ '--list-w': `${auditListWidth}px` }}
        >
          {selectedTab === 1 && (
            <AuditList
              key='jobs'
              columns={JOB_COLUMNS}
              rows={sessionData}
              getRowId={r => r.jobId}
              selectedId={selectedJob?.jobId}
              onSelect={setSelectedJob}
              cellCtx={{
                onStrike: handleStartButtonClick,
                userId: userType?.id
              }}
              visibleColumns={visibleColumns}
              onOpenColumnSettings={() => setColumnSettingsVisible(true)}
              onExportReady={fn => setExportFunction(() => fn)}
              clearFiltersTrigger={clearFiltersTrigger}
              loading={loading}
              storageKey='audit_list_jobs'
              searchPlaceholder='Search jobs…'
              emptyText='No jobs match your search.'
              defaultSort={{ key: 'submissionTime', dir: 'desc' }}
              exportName='audit_jobs'
            />
          )}
          {selectedTab === 2 && (
            <AuditList
              key='users'
              columns={USER_COLUMNS}
              rows={sessionData}
              getRowId={r => r.id}
              selectedId={selectedSession?.id}
              onSelect={setSelectedSession}
              visibleColumns={visibleColumns}
              onOpenColumnSettings={() => setColumnSettingsVisible(true)}
              onExportReady={fn => setExportFunction(() => fn)}
              clearFiltersTrigger={clearFiltersTrigger}
              loading={loading}
              storageKey='audit_list_users'
              searchPlaceholder='Search users…'
              emptyText='No users match your search.'
              defaultSort={{ key: 'loginTime', dir: 'desc' }}
              exportName='audit_users'
            />
          )}
          {selectedTab === 3 && (
            <AuditList
              key='facilities'
              columns={FACILITY_COLUMNS}
              rows={facilityAuditData}
              getRowId={r => r.facilityId}
              selectedId={selectedFacility?.facilityId}
              onSelect={setSelectedFacility}
              onExportReady={fn => setExportFunction(() => fn)}
              clearFiltersTrigger={clearFiltersTrigger}
              loading={loading}
              storageKey='audit_list_facilities'
              searchPlaceholder='Search facilities…'
              emptyText='No facilities match your search.'
              defaultSort={{
                key: 'facilityDisableStatusLastUpdated',
                dir: 'desc'
              }}
              exportName='audit_facilities'
            />
          )}
          <Splitter
            role='separator'
            aria-orientation='vertical'
            onMouseDown={startAuditListResize}
          />
          {selectedTab === 1 && (
            <AuditDetailPanel
              session={selectedJob}
              onClose={() => setSelectedJob(null)}
            />
          )}
          {selectedTab === 2 && (
            <AuditUserDetail
              session={selectedSession}
              onClose={() => setSelectedSession(null)}
            />
          )}
          {selectedTab === 3 && (
            <AuditFacilityDetail
              facility={selectedFacility}
              onClose={() => setSelectedFacility(null)}
            />
          )}
        </SplitShell>
        <ConfirmationModal
          antdModalProps={{
            centered: true,
            width: '300',
            closable: false
          }}
          visible={releasetModalVisible}
          onCancel={() => {
            setReleaseModalVisible(false);
          }}
          onOk={() => openModal()}
          header={
            <CustomTypography color={theme['@text-color']}>
              Confirm
            </CustomTypography>
          }
          confirmation={true}
          loading={releaseModalLoading}
        >
          <h1
            style={{ fontSize: '18px' }}
          >{`Are you sure you want to strike out the job? This action cannot be undone.`}</h1>
        </ConfirmationModal>
      </ContainerRow>
      <AuditColumnSettingsModal
        visible={columnSettingsVisible}
        onCancel={() => setColumnSettingsVisible(false)}
        onSave={handleSaveColumnSettings}
        visibleColumns={visibleColumns}
        selectedTab={selectedTab}
      />
    </>
  );
};

export default Audit;
